import React, { useState } from "react";
import styled from "styled-components";
import Button from "../../components/common/Button";
import { useRequest } from "../../hooks/useRequest";
import Progress from "../../components/common/Progress";
import { toast } from "react-toastify";
import SEO from "../../components/seo/SEO";

const CareersStyle = styled.div`
  padding: 60px 0;
  .container {
    max-width: 800px;
    margin: 0 auto;
  }
  .careers__header {
    text-align: center;
    margin-bottom: 50px;
    .page__title {
      color: #303030;
      font-size: 36px;
      font-weight: 600;
      line-height: 44px;
      margin-bottom: 16px;
    }
    .page__subtitle {
      color: #303030;
      font-size: 18px;
      font-weight: 400;
      line-height: 26px;
      margin-bottom: 8px;
    }
  }
  .careers__form {
    background: #fcfcfc;
    border-radius: 7.5px;
    border: 0.75px solid #d9d9d9;
    padding: 40px;
  }
  form {
    .form__group {
      margin-bottom: 24px;
      label {
        color: #303030;
        font-size: 15px;
        font-weight: 700;
        line-height: 22.5px;
        display: block;
        margin-bottom: 8.5px;
        &.required::after {
          content: " *";
          color: #ae0000;
        }
      }
      input,
      textarea,
      select {
        border-radius: 7.5px;
        border: 0.75px solid #30303040;
        background: #fff;
        width: 100%;
        padding: 16px 24px;
        font-size: 15px;
        font-family: inherit;
        &:focus {
          outline: none;
          border-color: #ae0000;
        }
      }
      input {
        height: 60px;
      }
      textarea {
        min-height: 120px;
        resize: vertical;
      }
      select {
        height: 60px;
        cursor: pointer;
      }
    }
    .form__wrapper {
      display: flex;
      gap: 12px;
      @media (max-width: 768px) {
        flex-direction: column;
      }
    }
    .submit__button__wrapper {
      margin-top: 32px;
      display: flex;
      justify-content: center;
    }
  }
  @media (max-width: 768px) {
    padding: 40px 20px;
    .careers__header {
      margin-bottom: 30px;
      .page__title {
        font-size: 28px;
        line-height: 36px;
      }
      .page__subtitle {
        font-size: 16px;
        line-height: 24px;
      }
    }
    .careers__form {
      padding: 24px;
    }
  }
`;

const Careers = () => {
    const [careerInfo, setCareerInfo] = useState({
        name: "",
        email: "",
        phone: "",
        position: "",
        experience: "",
        message: "",
    });
    const [sendApplication, { isLoading }] = useRequest();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const response = await sendApplication({
            path: `/careers`,
            method: "POST",
            body: JSON.stringify(careerInfo),
        });
        if (response.success) {
            toast.success(response.message || "Application submitted successfully!", { toastId: "success" });
            setCareerInfo({
                name: "",
                email: "",
                phone: "",
                position: "",
                experience: "",
                message: "",
            });
        } else {
            toast.error(response.message || "Failed to submit application.", { toastId: "error" });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCareerInfo((prev) => ({ ...prev, [name]: value }));
    };

    const experienceOptions = [
        "Less than 1 year",
        "1-2 years",
        "2-5 years",
        "5-10 years",
        "10+ years",
    ];

    return (
        <>
            <SEO
                title="Careers - Join Our Team | Instabuild Building Materials"
                description="Join the Instabuild team! Explore career opportunities in building materials, construction, and retail. Submit your application today."
                keywords="careers, jobs, employment, Instabuild careers, building materials jobs, construction jobs"
            />
            <CareersStyle>
                <div className="container">
                    <div className="careers__header">
                        <h1 className="page__title">Join Our Team</h1>
                        <p className="page__subtitle">
                            We're always looking for talented individuals to join our growing team.
                        </p>
                        <p className="page__subtitle">
                            Fill out the form below and we'll get back to you soon!
                        </p>
                    </div>
                    <div className="careers__form">
                        <form onSubmit={handleSubmit}>
                            <div className="form__group">
                                <label className="required">Full Name</label>
                                <input
                                    value={careerInfo?.name}
                                    type="text"
                                    name="name"
                                    placeholder="Enter your full name"
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form__wrapper">
                                <div className="form__group">
                                    <label className="required">Email Address</label>
                                    <input
                                        value={careerInfo?.email}
                                        type="email"
                                        name="email"
                                        placeholder="Enter your email"
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form__group">
                                    <label className="required">Phone Number</label>
                                    <input
                                        value={careerInfo?.phone}
                                        type="tel"
                                        name="phone"
                                        placeholder="Enter your phone number"
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form__wrapper">
                                <div className="form__group">
                                    <label className="required">Position Applied For</label>
                                    <input
                                        value={careerInfo?.position}
                                        type="text"
                                        name="position"
                                        placeholder="e.g., Sales Associate, Warehouse Manager"
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form__group">
                                    <label className="required">Years of Experience</label>
                                    <select
                                        value={careerInfo?.experience}
                                        name="experience"
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select experience level</option>
                                        {experienceOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="form__group">
                                <label className="required">Tell us about yourself</label>
                                <textarea
                                    value={careerInfo?.message}
                                    name="message"
                                    placeholder="Tell us about your background, skills, and why you'd like to join our team..."
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="submit__button__wrapper">
                                <Button title={"Submit Application"} />
                            </div>
                        </form>
                    </div>
                </div>
            </CareersStyle>
            {isLoading && <Progress />}
        </>
    );
};

export default Careers;

